import Constants from '@/Constants';
import Tag from '@/models/Tag';
import { ApplicationState } from '@/store';
import { getTagsClear, getTagsRequest } from '@/store/tag/action';
import React, { useEffect, useState } from 'react';
import { AsyncTypeahead, Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { useDispatch, useSelector } from 'react-redux';
import './style.scss';

interface Typeahead {
    [func: string]: CallableFunction;
}

const TagsInput = (props: any) => {
    const [tags, setTags] = useState<Tag[]>([]);
    const [isFocused, setIsFocused] = useState(false);
    const { hasError } = props;
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { orgId, applicationId } = useSelector((state: ApplicationState) => state.identity.data.user);
    const refTypeahead: React.RefObject<Typeahead> = React.createRef();

    const [options, setOptions] = useState<Tag[]>([]);
    const { suggestionTags } = useSelector((state: ApplicationState) => state.tags.data);
    const initialTags = useSelector((state: ApplicationState) => state.tags.data.tags);

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(
            getTagsRequest({
                orgId,
                applicationId,
                limit: Constants.TAG_REQUEST_DEFAULT_LIMIT,
                offset: Constants.TAG_REQUEST_DEFAULT_OFFSET,
            }),
        );
        return () => {
            dispatch(getTagsClear());
        };
    }, [dispatch, orgId, applicationId]);

    useEffect(() => {
        if (props.selectedTags) setTags(props.selectedTags);
    }, [props.selectedTags]);

    const handleOnKeyDown = (tagName: string) => {
        if (tagName.trim().length === 0 || isTagAlreadySelected(tagName)) {
            refTypeahead && refTypeahead.current && refTypeahead.current.blur();
            clearInput();
            return;
        }

        const mergedList = initialTags.concat(options);

        const tagsFound: Tag[] = mergedList.filter((tag: Tag) => isTagNamesEqual(tagName, tag.name));

        if (tagsFound.length) {
            addExistingTag(tagsFound[0]);
        } else {
            setTags((prevState): any => [
                ...prevState,
                {
                    applicationId: Constants.APP_ID,
                    id: null,
                    name: tagName,
                    organizationId: orgId,
                },
            ]);
            props.setTags('tags', [
                ...tags,
                {
                    applicationId: Constants.APP_ID,
                    id: null,
                    name: tagName,
                    organizationId: orgId,
                },
            ]);
            clearInput();
        }
    };

    const addExistingTag = (tag: Tag) => {
        setTags((prevState): Tag[] => [...prevState, tag]);
        props.setTags('tags', [...tags, tag]);
        clearInput();
    };

    const isTagAlreadySelected = (tagName: string): boolean => {
        return tags.findIndex((tag: Tag) => isTagNamesEqual(tagName, tag.name)) > -1;
    };

    const isTagNamesEqual = (tag1: string, tag2: string): boolean => tag1.localeCompare(tag2, undefined, { sensitivity: 'base' }) === 0;

    const addNewTag = (tagToAdd: Tag) => {
        if (isTagAlreadySelected(tagToAdd.name)) {
            clearInput();
            return;
        }

        const tagsFound: Tag[] = initialTags.filter((tag: Tag) => isTagNamesEqual(tagToAdd.name, tag.name));

        if (tagsFound.length) {
            addExistingTag(tagsFound[0]);
        } else {
            setTags((prevState): any => [
                ...prevState,
                {
                    applicationId: Constants.APP_ID,
                    id: tagToAdd.id,
                    name: tagToAdd.name,
                    organizationId: tagToAdd.organizationId,
                },
            ]);
            props.setTags('tags', [
                ...tags,
                {
                    applicationId: Constants.APP_ID,
                    id: tagToAdd.id,
                    name: tagToAdd.name,
                    organizationId: tagToAdd.organizationId,
                },
            ]);
            clearInput();
        }
    };

    const handleOnClick = (tag: Tag) => {
        addExistingTag(tag);
    };

    const removeTags = (tagListAfterRemoval: Tag[]) => {
        setTags(tagListAfterRemoval);
        props.setTags('tags', tagListAfterRemoval);
    };

    const handleOnChange = (typeaheadTagList: Tag[]) => {
        if (typeaheadTagList.length < tags.length) {
            removeTags(typeaheadTagList);
        } else {
            if (typeaheadTagList.length > 0 && typeaheadTagList[typeaheadTagList.length - 1].name) {
                const tagToAdd: Tag = typeaheadTagList[typeaheadTagList.length - 1] as Tag;

                addNewTag({
                    name: tagToAdd.name,
                    id: typeof tagToAdd.id !== 'number' ? null : tagToAdd.id,
                    organizationId: tagToAdd.organizationId || orgId,
                } as Tag);
            }
        }
    };

    const clearInput = () => {
        refTypeahead && refTypeahead.current && refTypeahead.current.clear();
    };

    const handleSearch = (search: string) => {
        dispatch(
            getTagsRequest({
                orgId,
                applicationId,
                limit: Constants.TAG_REQUEST_DEFAULT_LIMIT,
                offset: Constants.TAG_REQUEST_DEFAULT_OFFSET,
                name: search,
                isSuggestion: true,
            }),
        );
    };

    useEffect(() => {
        if (suggestionTags && suggestionTags.length > 0) {
            setOptions(suggestionTags);
        }
    }, [suggestionTags]);

    return (
        <>
            <div className={` d-flex flex-wrap tags-input ${isFocused ? 'focused' : ''} ${hasError ? 'error' : ''}`}>
                <AsyncTypeahead
                    allowNew
                    className="tag-typeahead-input"
                    id="tag-input-typeahead"
                    isLoading={false}
                    labelKey="name"
                    minLength={3}
                    multiple
                    newSelectionPrefix={strings.ADD_NEW_TAG}
                    onBlur={() => setIsFocused(false)}
                    onChange={(tags: Tag[]) => handleOnChange(tags)}
                    onFocus={() => setIsFocused(true)}
                    onKeyDown={(event: KeyboardEvent) => event.key === 'Enter' && handleOnKeyDown((event.target as HTMLInputElement).value)}
                    onSearch={handleSearch}
                    options={options}
                    placeholder={strings.TYPE_TAG}
                    positionFixed={true}
                    ref={refTypeahead}
                    selected={tags}
                />
            </div>

            <div className="row tags">
                <div className="row-tags">
                    {initialTags &&
                        initialTags.map((tag: Tag) => (
                            <div
                                className={`col-xs-2 tag ${tags.filter((t: Tag) => t.id === tag.id).length > 0 ? 'active' : ''}`}
                                key={tag.id}
                                onClick={() => {
                                    if (tags.filter((t: Tag) => t.id === tag.id).length <= 0) {
                                        handleOnClick(tag);
                                    }
                                }}
                                onKeyDown={() => {}}
                                role="button"
                                tabIndex={0}
                            >
                                <span>{tag.name}</span>
                            </div>
                        ))}
                </div>
            </div>
        </>
    );
};

export default TagsInput;
